import { useState, useRef, useEffect } from "react";
import { Search, SlidersHorizontal, X, CalendarIcon, ChevronUp, ChevronDown, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

export interface FilterGroup {
  key: string;
  label: string;
  options: string[];
  /** Optional grouped options: renders each group with a sub-header */
  groupedOptions?: { groupLabel: string; options: string[] }[];
  selected: Set<string>;
  onToggle: (value: string) => void;
  onClear: () => void;
}

interface TableFilterBarProps {
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  filterGroups?: FilterGroup[];
  filterOpen?: boolean;
  onFilterOpenChange?: (open: boolean) => void;
  expandedGroups?: Record<string, boolean>;
  onExpandedGroupsChange?: (groups: Record<string, boolean>) => void;
  dateRange?: {
    start: Date | undefined;
    end: Date | undefined;
    onStartChange: (date: Date | undefined) => void;
    onEndChange: (date: Date | undefined) => void;
    onClear: () => void;
  };
  /** Extra elements to render after the filter button (e.g. year selector) */
  extraControls?: React.ReactNode;
  /** Extra elements to render between search and the filter button */
  leadingControls?: React.ReactNode;
  /** Number of columns to use in the filter grid when panel is open */
  filterColumns?: number;
  /** On mobile, place the search input above the other controls */
  searchFirstOnMobile?: boolean;
  /** On desktop, place the search input at the far left and keep other controls in a separate right-aligned row */
  searchFirstOnDesktop?: boolean;
  /** On mobile, show a readable filter summary inside the trigger */
  showMobileFilterSummary?: boolean;
  /** Optional controls to render next to the mobile search input */
  mobileSearchTrailingControls?: React.ReactNode;
  /** Hide leading controls from the second mobile row when duplicated above */
  hideLeadingControlsOnMobile?: boolean;
  /** On mobile, place the controls row above the search row */
  mobileControlsFirst?: boolean;
  /** Use a compact desktop-only style for filter trigger and panel controls */
  compactDesktopFilters?: boolean;
}

function CollapsibleFilterGroup({ group, expanded, onToggleExpanded, inlineExpanded = false, compact = false }: { group: FilterGroup; expanded: boolean; onToggleExpanded: () => void; inlineExpanded?: boolean; compact?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!expanded) setQuery("");
  }, [expanded]);

  // Close on outside click
  useEffect(() => {
    if (!expanded) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onToggleExpanded();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [expanded, onToggleExpanded]);

  const normalizedQuery = query.trim().toLowerCase();
  const matchesQuery = (opt: string) => opt.toLowerCase().includes(normalizedQuery);

  const groupedFiltered = group.groupedOptions
    ? group.groupedOptions
      .map((grp) => ({ ...grp, options: grp.options.filter(matchesQuery) }))
      .filter((grp) => grp.options.length > 0)
    : [];

  const flatFiltered = group.options.filter(matchesQuery);
  const hasResults = group.groupedOptions ? groupedFiltered.length > 0 : flatFiltered.length > 0;
  const optionsContainerClass = cn(
    "overflow-y-auto overscroll-contain pr-1",
    inlineExpanded
      ? "max-h-[min(24rem,calc(100dvh-18rem))]"
      : "max-h-[min(18rem,calc(100dvh-16rem))]"
  );
  const dropdownContent = (
    <div className={cn(compact ? "space-y-1" : "space-y-1.5")}>
      <div className="relative">
        <Search className={cn("absolute top-1/2 -translate-y-1/2 text-muted-foreground", compact ? "left-1.5 h-3 w-3" : "left-2.5 h-3.5 w-3.5")} />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search the list"
          className={cn(compact ? "h-9 rounded-md border-slate-200 pl-6 text-[11px]" : "h-9 rounded-lg border-slate-200 pl-8 text-sm")}
        />
      </div>
      <div className={optionsContainerClass}>
        {group.groupedOptions ? (
          groupedFiltered.map((grp) => (
            <div key={grp.groupLabel}>
              <p className={cn("mb-1 font-semibold text-muted-foreground/60 uppercase tracking-wider", compact ? "text-[9px]" : "text-[10px]")}>{grp.groupLabel}</p>
              <div className={cn(compact ? "space-y-0" : "space-y-0.5")}>
                {grp.options.map((opt) => {
                  const isActive = group.selected.has(opt);
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => group.onToggle(opt)}
                      className={cn(
                        "w-full flex items-center hover:bg-slate-50 transition-colors",
                        compact ? "min-h-7 gap-1.5 rounded px-1.5 py-[3px]" : "min-h-9 gap-2.5 rounded-md px-2 py-1.5"
                      )}
                    >
                      <span
                        className={cn(
                          "inline-flex items-center justify-center shrink-0 rounded border-[1.5px]",
                          compact ? "h-3 w-3" : "h-4 w-4",
                          isActive ? "bg-[#1e3a8a] border-[#1e3a8a]" : "bg-white border-slate-300"
                        )}
                      >
                        {isActive && <Check className={cn(compact ? "h-2 w-2" : "h-2.5 w-2.5", "text-white")} />}
                      </span>
                      <span className={cn("leading-tight text-foreground text-left", compact ? "text-[11px]" : "text-sm")}>{opt}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))
        ) : (
          <div className={cn(compact ? "space-y-0" : "space-y-0.5")}>
            {flatFiltered.map((opt) => {
              const isActive = group.selected.has(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => group.onToggle(opt)}
                  className={cn(
                    "w-full flex items-center hover:bg-slate-50 transition-colors",
                    compact ? "min-h-7 gap-1.5 rounded px-1.5 py-[3px]" : "min-h-9 gap-2.5 rounded-md px-2 py-1.5"
                  )}
                >
                  <span
                    className={cn(
                      "inline-flex items-center justify-center shrink-0 rounded border-[1.5px]",
                      compact ? "h-3 w-3" : "h-4 w-4",
                      isActive ? "bg-[#1e3a8a] border-[#1e3a8a]" : "bg-white border-slate-300"
                    )}
                  >
                    {isActive && <Check className={cn(compact ? "h-2 w-2" : "h-2.5 w-2.5", "text-white")} />}
                  </span>
                  <span className={cn("leading-tight text-foreground text-left", compact ? "text-[11px]" : "text-sm")}>{opt}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
      {!hasResults && (
        <p className={cn("text-muted-foreground", compact ? "px-1 py-0.5 text-[10px]" : "px-1 py-1 text-[11px]")}>No options found.</p>
      )}
    </div>
  );

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={onToggleExpanded}
        className={cn(
          "flex w-full items-center cursor-pointer border transition-colors",
          compact ? "h-8 gap-1.5 rounded-md px-2 py-1.5 text-[11px]" : "h-10 gap-2 rounded-lg px-3 py-2 text-xs",
          group.selected.size > 0
            ? "border-[#1e3a8a] bg-[#eef4ff]"
            : expanded
              ? "border-slate-300 bg-slate-100"
              : "border-slate-200 bg-slate-50 hover:bg-slate-100"
        )}
      >
        <p className={cn("font-semibold text-foreground/70 uppercase tracking-wider", compact ? "text-[10px]" : "text-[11px]")}>{group.label}</p>
        <div className="ml-auto inline-flex items-center gap-0.5">
          {group.selected.size > 0 && (
            <>
              <span
                className={cn(
                  "inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground font-semibold",
                  compact ? "h-4 min-w-4 px-1 text-[9px]" : "h-5 min-w-5 px-1.5 text-[10px]"
                )}
              >
                {group.selected.size}
              </span>
              <button
                type="button"
                className={cn("inline-flex items-center justify-center rounded-sm text-[#1e3a8a] hover:bg-[#dbe8ff]", compact ? "h-3.5 w-3.5" : "h-4 w-4")}
                onClick={(e) => {
                  e.stopPropagation();
                  group.onClear();
                }}
                aria-label={`Clear ${group.label} filter`}
              >
                <X className={cn(compact ? "h-2.5 w-2.5" : "h-3 w-3")} />
              </button>
            </>
          )}
          <ChevronDown className={cn(compact ? "h-3 w-3" : "h-3.5 w-3.5", "text-muted-foreground transition-transform duration-200", expanded && "rotate-180")} />
        </div>
      </button>

      {expanded && (
        inlineExpanded ? (
          <div className={cn("mt-2 rounded-lg border border-border bg-card shadow-sm", compact ? "p-1.5" : "p-2")}>
            {dropdownContent}
          </div>
        ) : (
          <div className={cn(
            "absolute top-full left-0 mt-1 z-[60] bg-white border border-slate-200 shadow-lg animate-in fade-in-0 slide-in-from-top-2 duration-150",
            compact ? "w-[min(13rem,calc(100vw-5rem))] rounded-md p-1.5" : "w-[min(18rem,calc(100vw-5rem))] rounded-lg p-3"
          )}>
            {dropdownContent}
          </div>
        )
      )}
    </div>
  );
}

export default function TableFilterBar({
  search,
  onSearchChange,
  searchPlaceholder = "Search...",
  filterGroups = [],
  filterOpen: filterOpenProp,
  onFilterOpenChange,
  expandedGroups: expandedGroupsProp,
  onExpandedGroupsChange,
  dateRange,
  extraControls,
  leadingControls,
  filterColumns = 4,
  searchFirstOnMobile = false,
  searchFirstOnDesktop = false,
  showMobileFilterSummary = false,
  mobileSearchTrailingControls,
  hideLeadingControlsOnMobile = false,
  mobileControlsFirst = false,
  compactDesktopFilters = false,
}: TableFilterBarProps) {
  const [filterOpenLocal, setFilterOpenLocal] = useState(false);
  const [expandedGroupsLocal, setExpandedGroupsLocal] = useState<Record<string, boolean>>({});
  const [mobileActiveGroupKey, setMobileActiveGroupKey] = useState<string | null>(null);
  const [filterPanelLeft, setFilterPanelLeft] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const filterTriggerRef = useRef<HTMLButtonElement>(null);
  const isMobile = useIsMobile();

  const filterOpen = filterOpenProp !== undefined ? filterOpenProp : filterOpenLocal;
  const computeFilterPanelLeft = () => {
    if (!containerRef.current || !filterTriggerRef.current) return 0;
    const containerRect = containerRef.current.getBoundingClientRect();
    const triggerRect = filterTriggerRef.current.getBoundingClientRect();
    return Math.max(0, triggerRect.left - containerRect.left);
  };
  const closeExpandedGroups = () => { setExpandedGroupsLocal({}); onExpandedGroupsChange?.({}); };
  const setFilterOpen = (val: boolean) => {
    if (val && !isMobile) {
      setFilterPanelLeft(computeFilterPanelLeft());
    }
    setFilterOpenLocal(val);
    onFilterOpenChange?.(val);
    if (!val) {
      closeExpandedGroups();
      setMobileActiveGroupKey(null);
    }
  };

  // Close filter panel on outside click
  useEffect(() => {
    if (!filterOpen || isMobile) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;

      // Date popovers render in a portal outside the filter container.
      // Treat interactions inside them as "inside" so the panel stays open.
      if (target instanceof Element && target.closest('[data-filter-date-popover="true"]')) {
        return;
      }

      if (containerRef.current && !containerRef.current.contains(target)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [filterOpen, isMobile]);
  const expandedGroups = expandedGroupsProp !== undefined ? expandedGroupsProp : expandedGroupsLocal;
  const toggleGroupExpanded = (key: string) => {
    const next = { ...expandedGroups, [key]: !expandedGroups[key] };
    setExpandedGroupsLocal(next);
    onExpandedGroupsChange?.(next);
  };

  const totalActive =
    filterGroups.reduce((sum, g) => sum + g.selected.size, 0) +
    (dateRange?.start || dateRange?.end ? 1 : 0);

  const clearAll = () => {
    filterGroups.forEach((g) => g.onClear());
    dateRange?.onClear();
  };

  const firstSelectedGroup = filterGroups.find((group) => group.selected.size > 0);
  const firstSelectedValue = firstSelectedGroup ? Array.from(firstSelectedGroup.selected)[0] : undefined;
  const mobileFilterSummary = totalActive === 0
    ? "Filters"
    : firstSelectedValue
      ? firstSelectedValue
      : dateRange?.start || dateRange?.end
        ? "Date"
        : "Filters";
  const resolvedFilterColumns = Math.max(1, Math.min(filterColumns, filterGroups.length || 1));
  const mobileActiveGroup = mobileActiveGroupKey
    ? filterGroups.find((group) => group.key === mobileActiveGroupKey) ?? null
    : null;
  const useCompactDesktop = compactDesktopFilters && !isMobile;

  useEffect(() => {
    if (isMobile || !filterOpen) return;

    const syncFilterPanelLeft = () => {
      setFilterPanelLeft(computeFilterPanelLeft());
    };

    syncFilterPanelLeft();
    window.addEventListener("resize", syncFilterPanelLeft);
    return () => window.removeEventListener("resize", syncFilterPanelLeft);
  }, [filterOpen, isMobile]);

  const renderFilterBody = (inlineExpanded: boolean) => (
    <>
      {filterGroups.length > 0 && (
        <div
          className="grid gap-1 items-start"
          style={{ gridTemplateColumns: `repeat(${inlineExpanded ? 1 : resolvedFilterColumns}, minmax(0, 1fr))` }}
        >
          {filterGroups.map((group) => {
            if (inlineExpanded && isMobile) {
              const hasSelection = group.selected.size > 0;

              return (
                <button
                  key={group.key}
                  type="button"
                  onClick={() => setMobileActiveGroupKey(group.key)}
                  className={cn(
                    "flex min-h-11 w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors",
                    hasSelection
                      ? "border-[#1e3a8a] bg-[#eef4ff]"
                      : "border-slate-200 bg-slate-50 hover:bg-slate-100"
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-foreground/70">{group.label}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {hasSelection ? `${group.selected.size} selected` : "Choose options"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {hasSelection && (
                      <Badge className="h-5 min-w-5 rounded-full bg-primary px-1.5 text-[10px] text-primary-foreground">
                        {group.selected.size}
                      </Badge>
                    )}
                    <ChevronDown className="h-4 w-4 -rotate-90 text-muted-foreground" />
                  </div>
                </button>
              );
            }

            return (
              <CollapsibleFilterGroup
                key={group.key}
                group={group}
                expanded={!!expandedGroups[group.key]}
                onToggleExpanded={() => toggleGroupExpanded(group.key)}
                inlineExpanded={inlineExpanded}
                compact={useCompactDesktop}
              />
            );
          })}
        </div>
      )}

      {dateRange && (
        <div className={cn(filterGroups.length > 0 && "mt-4 pt-3 border-t border-border")}>
          <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Date Range</p>
          <div className={cn("gap-2", inlineExpanded ? "grid grid-cols-1" : "grid grid-cols-2 max-w-md")}>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className={cn("w-full justify-start text-left font-normal h-9 text-xs", !dateRange.start && "text-muted-foreground")}>
                  <CalendarIcon className="w-3.5 h-3.5 mr-1.5" />
                  {dateRange.start ? format(dateRange.start, "MMM d, yyyy") : "Start date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent data-filter-date-popover="true" className="z-[70] w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dateRange.start}
                  fixedWeeks
                  defaultMonth={dateRange.start ?? new Date()}
                  onSelect={dateRange.onStartChange}
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className={cn("w-full justify-start text-left font-normal h-9 text-xs", !dateRange.end && "text-muted-foreground")}>
                  <CalendarIcon className="w-3.5 h-3.5 mr-1.5" />
                  {dateRange.end ? format(dateRange.end, "MMM d, yyyy") : "End date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent data-filter-date-popover="true" className="z-[70] w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dateRange.end}
                  fixedWeeks
                  defaultMonth={dateRange.end ?? dateRange.start ?? new Date()}
                  onSelect={dateRange.onEndChange}
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
          </div>
          {(dateRange.start || dateRange.end) && (
            <Button variant="ghost" size="sm" className="h-6 text-xs text-muted-foreground mt-1 px-1.5" onClick={dateRange.onClear}>
              <X className="w-3 h-3 mr-1" /> Clear dates
            </Button>
          )}
        </div>
      )}
    </>
  );

  return (
    <div className="relative" ref={containerRef}>
      <div className={cn("flex gap-2 sm:gap-3", searchFirstOnMobile && (mobileControlsFirst ? "flex-col-reverse" : "flex-col"))}>
        {/* Search row on mobile when requested; inline on larger screens. */}
        <div className={cn(
          searchFirstOnMobile
            ? "flex w-full min-w-0 items-center gap-3 sm:hidden"
            : searchFirstOnDesktop
              ? "hidden w-72 min-w-[180px] sm:block"
              : "hidden"
        )}>
          <div className={cn("relative", searchFirstOnMobile ? "min-w-0 flex-1" : "w-72 min-w-[180px]") }>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground z-10 pointer-events-none" />
            <Input
              placeholder={searchPlaceholder}
              className="pl-9 h-8 text-[13px] sm:h-9 sm:text-sm hover:bg-hover hover:border-primary/30 hover:shadow-sm focus:placeholder:text-transparent focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>
          {searchFirstOnMobile && mobileSearchTrailingControls && (
            <div className="shrink-0">{mobileSearchTrailingControls}</div>
          )}
        </div>

        <div className={cn(
          "flex items-center gap-1.5 sm:gap-3",
          "w-full",
          searchFirstOnMobile && "justify-between"
        ) }>
          <div className="relative flex items-center gap-1.5 sm:gap-3 flex-1 min-w-0">
          {/* Left: view toggle + filters */}
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            {!(hideLeadingControlsOnMobile && searchFirstOnMobile) && leadingControls}

            {(filterGroups.length > 0 || dateRange) && (
              <Button
                ref={filterTriggerRef}
                variant="outline"
                size="sm"
                className={cn(
                  "h-8 gap-1.5 px-2.5 text-xs rounded-lg",
                  useCompactDesktop
                    ? "sm:h-9 sm:w-[150px] sm:gap-1.5 sm:px-2 sm:text-sm"
                    : "sm:h-9 sm:gap-2 sm:px-3 sm:text-sm"
                )}
                onClick={() => setFilterOpen(!filterOpen)}
              >
                <SlidersHorizontal className={cn("w-3.5 h-3.5", useCompactDesktop ? "sm:w-3.5 sm:h-3.5" : "sm:w-4 sm:h-4")} />
                <span>Filters</span>
                {totalActive > 0 && (
                  <Badge className={cn(
                    "font-bold bg-primary text-primary-foreground rounded-full text-[10px]",
                    useCompactDesktop ? "h-4 min-w-4 px-1" : "h-5 min-w-5 px-1.5"
                  )}>
                    {totalActive}
                  </Badge>
                )}
                <ChevronUp className={cn(
                  "transition-transform duration-200",
                  useCompactDesktop ? "w-3 h-3" : "w-3.5 h-3.5",
                  !filterOpen && "rotate-180"
                )} />
              </Button>
            )}
          </div>

          {/* Center: search */}
            <div className={cn(
              "relative flex-1 min-w-[140px]",
              searchFirstOnMobile && "hidden sm:block"
            ) }>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground z-10 pointer-events-none" />
            <Input
              placeholder={searchPlaceholder}
              className="pl-10 h-9 hover:bg-hover hover:border-primary/30 hover:shadow-sm focus:placeholder:text-transparent focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>

          </div>

          {/* Right: extra controls */}
          <div className={cn("shrink-0", searchFirstOnMobile ? "ml-0" : "ml-auto") }>
            {extraControls}
          </div>
        </div>
      </div>

      {/* Floating filter panel overlay – anchored to full-width container */}
      {!isMobile && filterOpen && (filterGroups.length > 0 || dateRange) && (
        <div className={cn(
          "absolute top-full z-50 mt-1.5 w-auto bg-white border border-slate-200 shadow-md animate-in fade-in-0 slide-in-from-top-2 duration-200",
          useCompactDesktop ? "max-w-md rounded-lg p-2" : "max-w-lg rounded-xl p-3"
        )} style={{ left: filterPanelLeft }}>
          {renderFilterBody(false)}

          <div className={cn("flex items-center justify-end gap-2", useCompactDesktop ? "mt-2" : "mt-3")}>
            {totalActive > 0 && (
              <Button variant="ghost" size="sm" className={cn("text-xs text-muted-foreground", useCompactDesktop ? "h-7 px-2" : "h-8")} onClick={clearAll}>Clear all</Button>
            )}
            <Button size="sm" className={cn("text-xs font-medium", useCompactDesktop ? "h-7 px-3" : "h-8 px-5")} onClick={() => setFilterOpen(false)}>Done</Button>
          </div>
        </div>
      )}

      {isMobile && (filterGroups.length > 0 || dateRange) && (
        <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
          <SheetContent side="bottom" className="rounded-t-3xl px-4 pb-4 pt-5 max-h-[85dvh] overflow-y-auto md:hidden">
            <SheetHeader className="mb-4 text-left">
              <SheetTitle className="font-heading text-base">Filters</SheetTitle>
            </SheetHeader>

            {renderFilterBody(true)}

            <div className="mt-5 flex items-center gap-2 border-t border-border pt-4">
              {totalActive > 0 && (
                <Button variant="ghost" className="h-9 flex-1 text-sm" onClick={clearAll}>Clear all</Button>
              )}
              <Button className="h-9 flex-1 text-sm" onClick={() => setFilterOpen(false)}>Done</Button>
            </div>
          </SheetContent>
        </Sheet>
      )}

      {isMobile && mobileActiveGroup && (
        <Sheet open={!!mobileActiveGroup} onOpenChange={(open) => !open && setMobileActiveGroupKey(null)}>
          <SheetContent side="bottom" className="rounded-t-3xl px-4 pb-4 pt-5 max-h-[85dvh] overflow-y-auto md:hidden">
            <SheetHeader className="mb-4 text-left">
              <SheetTitle className="font-heading text-base">{mobileActiveGroup.label}</SheetTitle>
            </SheetHeader>

            <div className="rounded-xl border border-border bg-card p-3 shadow-sm">
              <CollapsibleFilterGroup
                group={mobileActiveGroup}
                expanded
                onToggleExpanded={() => setMobileActiveGroupKey(null)}
                inlineExpanded
              />
            </div>

            <div className="mt-5 flex items-center gap-2 border-t border-border pt-4">
              {mobileActiveGroup.selected.size > 0 && (
                <Button variant="ghost" className="h-9 flex-1 text-sm" onClick={mobileActiveGroup.onClear}>Clear all</Button>
              )}
              <Button className="h-9 flex-1 text-sm" onClick={() => setMobileActiveGroupKey(null)}>Done</Button>
            </div>
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}
